/**
 * Utility to get current user role
 * Reads from localStorage first, but can refresh from API if needed
 */

export const getUserRole = (): string | null => {
	const localInfo = localStorage.getItem('userInfo');
	if (!localInfo) {
		return null;
	}
	
	try {
		const userInfo = JSON.parse(localInfo);
		return userInfo?.role || null;
	} catch (error) {
		return null;
	}
}

export const isAdmin = (): boolean => {
	const role = getUserRole();
	return role === 'admin';
}

/**
 * Refresh user info from backend and update localStorage
 * This ensures the role is always current
 * Silently fails on network errors to prevent unwanted redirects
 */
export const refreshUserInfo = async (): Promise<{ role: string | null; isAdmin: boolean }> => {
	try {
		const { getUserInfo } = await import('@/request/api');
		const res = await getUserInfo();
		
		// Only update if we got a successful response
		// Note: axios interceptor returns res.data directly, so response is { code, email, role }
		if (res && res.code === 0) {
			const userInfo = {
				email: res.email,
				role: res.role || 'user'
			};
			localStorage.setItem('userInfo', JSON.stringify(userInfo));
			// Dispatch custom event to notify other components (e.g., homepage)
			window.dispatchEvent(new Event('userInfoChanged'));
			return {
				role: res.role || 'user',
				isAdmin: res.role === 'admin'
			};
		}
	} catch (error: any) {
		// Silently handle errors - don't log or throw
		// Network errors or backend restarts shouldn't cause redirects
		// Only actual token errors will be handled by the request interceptor
	}
	
	// Fallback to localStorage if API call fails
	const role = getUserRole();
	return {
		role,
		isAdmin: role === 'admin'
	};
}
